import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Selfbook**
 *
 * @exports bs_355
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};
  const isSpaHompageProperty = [1047167, 1160918].includes(thn.data.propertyId);
  let isBookingObserving = false;

  engine._getState = (stateProperty) => {
    const selfbookObject = window.selfbookWidgetStore?.getState();
    if (stateProperty) {
      return thn.objectGet(selfbookObject, stateProperty);
    }

    return selfbookObject;
  };

  /**
      * @memberOf module:bs_355
      * @see {@link onStateChange}
      *
      * @param {object} ctx - The context object.
      */
  engine.onStateChange = thn.debounce(function () {
    if (isSpaHompageProperty) {
      thn.pageCheck();
    }
  }, 500);

  /**
     * @memberOf module:bs_355
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {
      let language = engine._getState('locale');
      const excludeHotelsList = [1034784].includes(thn.data.propertyId);

      if (thn.data.propertyId === 1024366 && thn.empty(language)) {
        // window.selfbookWidgetStore object only exists once the BE has been opened
        language = document.querySelector('#w-dropdown-toggle-0')?.lang;
      }

      if (language && ctx.langs.test(language) && !excludeHotelsList) {
        resolve(language);
        return;
      }

      reject('no_language');
    });
  };

  /**
     * @memberOf module:bs_355
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot() {

    const dataLayer = window.dataLayer;
    if (dataLayer) {
      const refreshPage = ({ message }) => {
        if (message.ecommerce || message.event === 'sbOpened' || message.event === 'begin_checkout_sb' || message.event === 'eec.purchase') {
          thn.pageCheck();
        }
      };

      thn.bus.on('dataLayer:push', refreshPage);
      thn.shutdown(() => {
        thn.bus.detach('dataLayer:push', refreshPage);
      });
    }

    const container = document.querySelector('#selfbook_sdkwidget');
    if (!container) {
      return;
    }

    const handler = thn.debounce(() => {
      thn.languageCheck();
      setTimeout(thn.pageCheck, 500);
    },
    800);

    thn.observeDom(container, handler, {
      childList: true,
      subtree: true,
      characterData: false,
      attributes: true,
      attributeFilter: ['aria-label', 'style'],
    });

    thn.shutdown(() => {
      handler.cancel();
      thn.stopObserveDom(container, handler);
    });
  };

  /**
     * @memberOf module:bs_355
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    // Selfbook is a type of modal integrated into hotel pages that is activated through parameters.
    // Example: https://www.parkhotel-quellenhof.de/?startDate=2025-02-06&endDate=2025-02-07&locale=de&selfbook=true&sb-search-step=rooms-list
    const hotelId = engine._getState('hotelId') || engine._getState('id');
    const url = new URL(ctx.baseUrl);

    url.searchParams.set('startDate', '[date_begin]');
    url.searchParams.set('endDate', '[date_end]');
    url.searchParams.set('adult', '[adults]');
    url.searchParams.set('child', '[children]');
    url.searchParams.set('currency', '[currency]');
    url.searchParams.set('locale', '[language]');
    url.searchParams.set('selfbook', 'true');
    url.searchParams.set('sb-search-step', 'rooms-list');

    if (hotelId) { // This works for chains as they have to specify which hotel they need to perform the search.
      url.searchParams.set('hotel', hotelId);
    }

    ctx.newData.dateBegin = ctx.data.dateBegin?.replace(/\//g, '-');
    ctx.newData.dateEnd = ctx.data.dateEnd?.replace(/\//g, '-');
    ctx.newUrl = url.toString();

    return ctx;
  };

  /**
    * @memberOf module:bs_355
    * @see {@link is_user_logged}
    *
    * @param {object} ctx - The context object.
    */
  engine.isUserLogged = function isUserLogged(ctx) {
    const selfbookObjectAuth = engine._getState('auth'); // This function is present on all hotel pages
    const isLogged = !!selfbookObjectAuth?.isLoggedIn;
    return isLogged;
  };

  /**
   * @memberOf module:bs_355
   * @this thn.widgets.price
   * @see {@link Price#observeDom}
   *
   * @param {object} ctx - The context object.
   */
  engine.priceObserveDom = function priceObserveDom(ctx) {
    const dataLayer = window.dataLayer;

    if (dataLayer) {
      const refreshPrice = ({ message }) => {
        if (message.event === 'eec.impression') {
          ctx.widget.update();
        }
      };

      thn.bus.on('dataLayer:push', refreshPrice);

      thn.pageUnload(() => {
        thn.bus.detach('dataLayer:push', refreshPrice);
      });
    }
  };

  /**
    *
    * @memberOf module:bs_355
    *
    */
  engine._roomDetailsParseData = function roomDetailsParseData() {
    const selfbookRooms = engine._getState('getAvailability')?.data?.data;
    if (selfbookRooms) {
      const roomsTypes = {
        King: 'superior',
        Queen: 'superior',
        Suite: 'suite',
        Studio: 'standard',
      };
      const availableRates = thn.empty(selfbookRooms[0]?.rates) ? selfbookRooms[0].availability : selfbookRooms;
      const roomDetailsArray = availableRates.map((roomEl) => {
        const roomType = roomEl.room?.name?.match(/King|Queen|Suite|Studio/);
        const roomPrice = roomEl.rates?.map((priceEl) => {
          const refundable = thn.objectGet(priceEl, 'policies')?.[0]?.text[0]?.includes('Flexible');
          return {
            id: priceEl.id,
            name: priceEl.name,
            price: thn.parseNumber(priceEl.nightly_before_tax),
            refundable: refundable ? 1 : 0,
          };
        });
        return {
          id: roomEl.room?.id,
          name: roomEl.room?.name,
          adults: thn.parseNumber(roomEl.room?.room_features[1]?.name),
          children: 0,
          price: roomPrice,
          type: roomType?.length ? roomsTypes[roomType[0]] : 'standard',
        };
      });
      return roomDetailsArray;
    }
    return [];
  };

  /**
     * @memberOf module:bs_355
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};
    const selfbookObject = engine._getState();

    if (selfbookObject) {
      // Dates
      const dateBegin = translateDate((thn.objectGet(selfbookObject, 'start_date'))?.toString(), thn.data.language || 'en') || thn.dateToString(thn.objectGet(selfbookObject, 'start_date'));
      const dateEnd = translateDate((thn.objectGet(selfbookObject, 'end_date'))?.toString(), thn.data.language || 'en') || thn.dateToString(thn.objectGet(selfbookObject, 'end_date'));
      let nights;
      if (dateBegin && dateEnd) {
        widgetData.dateBegin = dateBegin;
        widgetData.dateEnd = dateEnd;
        nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
      }

      // Guests
      const guests = thn.objectGet(selfbookObject, 'bookingForm')?.guests;
      widgetData.adults = guests?.length > 0 ? guests[0]?.count : 2;
      widgetData.children = guests?.length > 1 ? guests[1]?.count : 0;
      widgetData.guests = widgetData.adults + widgetData.children;

      // Price and Currency
      const rooms = thn.objectGet(selfbookObject, 'getAvailability')?.data;
      const shouldUseSearchedPrices = [1041004, 1041003, 1037746, 1036861, 1024366].includes(thn.data.propertyId);
      const shouldUseBeforeTax = [1026274, 1037020, 1037018, 1037011, 1037007, 1037015, 1015353, 1051664, 1047167, 1033846, 1024898, 1024899, 1024900, 1029482, 1040693, 1055364, 1055365, 1049919, 1051646, 1051649, 1051650, 1051645, 1051647, 1040982, 1054287].includes(thn.data.propertyId);
      if (rooms && rooms.data && rooms.data.length) {
        let roomsMinPrices = rooms.data.map((room) => (room?.price_range?.min || shouldUseBeforeTax ? room?.lowest_base_before_tax : room?.lowest_base_after_tax)).filter(Boolean);
        roomsMinPrices = shouldUseSearchedPrices && rooms?.data[0].availability.map((room) => room.price_range.after_tax.min).flat();
        if (!roomsMinPrices.length) {
          const dlRoomsData = thn.objectGet(window.dataLayer, 'ecommerce', { first: false });
          roomsMinPrices = (dlRoomsData?.impressions || dlRoomsData?.items)
            ?.map((room) => thn.parseNumber(room?.price));
        }
        const currency = thn.objectGet(selfbookObject, 'currency_code');
        if (!thn.empty(roomsMinPrices) && currency && nights) {
          widgetData.price = Math.min(...roomsMinPrices);
          widgetData.currency = currency;
          widgetData.roomDetails = engine._roomDetailsParseData();
          widgetData.set = true;
        }
      }
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_355
     * @this thn.widgets.booking
     * @see {@link Booking#observe_dom}
     *
     * @param {object} ctx - The context object.
  */
  engine.bookingObserveDom = function bookingObserveDom(ctx) {

    if (isBookingObserving || ctx.widget.isSet()) {
      return;
    }

    isBookingObserving = true;
    const listenerBooking = thn.cancellable(() => {
      const infoMultiReservation = engine._getState('reservationDetails')?.multiInfo?.data;
      const infoReservation = engine._getState('info')?.data;

      if (thn.empty(infoReservation) && thn.empty(infoMultiReservation)) {
        return;
      }

      if (ctx.widget.setData().isSet()) {
        ctx.widget.save();
        isBookingObserving = false;
        listenerBooking.cancel();
      }
    });

    window.selfbookWidgetStore?.subscribe(listenerBooking);
  };

  /**
     * @memberOf module:bs_355
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};
    const infoReservation = engine._getState('info')?.data;
    const infoReservationMutiBookings = engine._getState('reservationDetails')?.multiInfo?.data;

    if (!thn.empty(infoReservationMutiBookings)) {
      this.logger.debug('infoReservationMutiBookings', infoReservationMutiBookings);

      const widgetDataMulti = infoReservationMutiBookings.rooms?.map((room) => ({
        bookingId: room.id,
        dateBegin: infoReservationMutiBookings?.start_date?.replace(/-/g, '/'),
        dateEnd: infoReservationMutiBookings?.end_date?.replace(/-/g, '/'),
        price: room.total?.after_tax,
        currency: room.total?.currency_code,
      }));

      return widgetDataMulti;
    }

    if (!thn.empty(infoReservation)) {
      this.logger.debug('infoReservation', infoReservation);

      const bookingId = infoReservation.id;

      if (!bookingId) {
        widgetData.set = false;
        return widgetData;
      }

      widgetData.bookingId = bookingId;
      widgetData.dateBegin = thn.objectGet(infoReservation, 'start_date')?.replace(/-/g, '/');
      widgetData.dateEnd = thn.objectGet(infoReservation, 'end_date')?.replace(/-/g, '/');
      widgetData.price = thn.objectGet(infoReservation, 'details')?.total?.afterTax || thn.objectGet(infoReservation, 'details')?.total?.after_tax;
      widgetData.currency = thn.objectGet(infoReservation, 'details')?.total?.currencyCode || thn.objectGet(infoReservation, 'details')?.total?.currency_code;

      return widgetData;
    }

    widgetData.set = false;

    return widgetData;
  };

  return engine;

}

export { factory };
